import { useFlag } from '@/components/posthog-provider';
import { queryKeys } from '@/lib/query-keys';
import { apiClient } from '@borradh-workspace/api-client';
import { STOCK_FOOTAGE_MATCHING_NEW_FLAG } from '@borradh-workspace/labels';
import { PROFILED_MUSIC_TRACKS } from '@borradh-workspace/video-templates/music-registry';
import { queryOptions, useQuery } from '@tanstack/react-query';

/** One track in the shared music library, as `GET /videos/music-tracks`
 * returns it — `url` is absolute and playable. */
export interface MusicTrack {
  id: string;
  name: string;
  artist?: string;
  mood?: string;
  /** Beats per minute — beat-synced templates cut on this grid. */
  bpm: number;
  durationSec: number;
  url: string;
}

export const listMusicTracksQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.videos.musicTracks(),
    queryFn: () =>
      apiClient.get<{ tracks: MusicTrack[] }>('videos/music-tracks'),
    // The library is static reference data.
    staleTime: 60 * 60 * 1000,
  });

export const useListMusicTracks = (enabled = true) => {
  const v2Enabled = useFlag(STOCK_FOOTAGE_MATCHING_NEW_FLAG);
  const query = useQuery({
    ...listMusicTracksQueryOptions(),
    queryKey: queryKeys.videos.musicTracks(v2Enabled),
    enabled,
  });
  return {
    tracks: (query.data?.tracks ?? []).filter(
      (track) =>
        v2Enabled ||
        !PROFILED_MUSIC_TRACKS.some((profiled) => profiled.id === track.id)
    ),
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
};
